import React, { forwardRef, useState, useRef, useImperativeHandle } from "react";
import router from "umi/router";
import { PageHeaderWrapper } from "@ant-design/pro-layout";
import ProTable, { IntlProvider, enUSIntl } from "@ant-design/pro-table";
import SortAndFilter, { DEFAULT_FILTERS } from "@/components/SortAndFilter";

// this tags represent interactive elements on table row
const ignoreTags = [
  "button",
  ".ant-select",
  ".ant-select-selector",
  ".ant-select-dropdown",
  ".ant-dropdown-menu",
  ".ant-modal-wrap",
].join(", ");

export function rowClick(record, baseUrl, parameterName, rowKey) {
  // Nissan tables are the only ones without onClick
  if (!baseUrl || !parameterName || !rowKey) return;

  return {
    onClick: event => {
      const tag = event.target.closest(ignoreTags);
      if (tag === null) {
        router.push(`${baseUrl}?${parameterName}=${record[rowKey]}`);
      }
    },
  };
}

const ListView = forwardRef(function ListView(
  {
    filterType,
    rowKey = "id",
    columns,
    request,
    filterDateRange = false,
    baseDetailsUrl = null,
    detailsUrlParamName = null,
    sortingSeparator = ":",
    tableStyle,
    rowSelection,
    expandable,
    toolBarRender,
    header,
    children,
    filtersChildren,
  },
  ref,
) {
  const filtersRef = useRef();
  const actionRef = useRef();

  const [filters, setFilters] = useState(null);

  useImperativeHandle(
    ref,
    () => {
      return {
        reload: () => {
          actionRef.current?.reload();
        },
        getFilters: () => filtersRef?.current?.getFilters(),
      };
    },
    [],
  );

  return (
    <PageHeaderWrapper ref={ref}>
      {header}

      <SortAndFilter
        ref={filtersRef}
        type={filterType}
        columns={columns}
        filterDateRange={filterDateRange}
        sortingSeparator={sortingSeparator}
        onFiltersChange={setFilters}
        onReset={() => {
          // ensure filters state updates and reload
          setFilters({ ...DEFAULT_FILTERS });
          actionRef?.current?.reload();
          window.history.replaceState({}, "", `${window.location.pathname}`);
        }}
      >
        {filtersChildren}
      </SortAndFilter>

      {filters !== null && (
        <IntlProvider value={enUSIntl}>
          <ProTable
            options={false}
            search={false}
            rowKey={rowKey}
            rowClassName="tableRow"
            className="vehiclesTableClickable"
            actionRef={actionRef}
            columns={columns}
            request={request}
            tableStyle={tableStyle}
            rowSelection={rowSelection}
            expandable={expandable}
            toolBarRender={toolBarRender}
            onRow={record => rowClick(record, baseDetailsUrl, detailsUrlParamName, rowKey)}
            params={{
              filters,
            }}
            pagination={{
              current: filters.page,
              pageSize: filters.pageSize,
              onChange: (page, pageSize) =>
                filtersRef?.current?.setPaginationFilters({
                  ...filters,
                  page,
                  pageSize,
                }),
              onShowSizeChange: (page, pageSize) =>
                filtersRef?.current?.setPaginationFilters({
                  ...filters,
                  page,
                  pageSize,
                }),
            }}
          />
        </IntlProvider>
      )}

      {children}
    </PageHeaderWrapper>
  );
});

export default ListView;
